import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserShield, faPlay } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import InternalShell, { useInternalApi } from '../layouts/InternalShell';
import { COMPLIANCE_NAV } from './nav';

interface Review {
    id: number; principal_type: string; principal_id: string; principal_label: string;
    grants_at_review: string[]; outcome: string; notes: string;
    reviewed_by: string | null; created_at: string; reviewed_at: string | null;
}
interface Candidate { principal_type: string; principal_id: string; grant_count: number; }

const OUTCOME_COLORS: Record<string, string> = {
    pending: 'warning', appropriate: 'success', reduced: 'info', revoked: 'danger',
};
const OUTCOMES = ['pending', 'appropriate', 'reduced', 'revoked'];

const AccessReviews: React.FC = () => {
    const { api } = useInternalApi();
    const [reviews, setReviews] = useState<Review[]>([]);
    const [candidates, setCandidates] = useState<Candidate[]>([]);
    const [loading, setLoading] = useState(true);
    const [viewing, setViewing] = useState<Review | null>(null);
    const [notes, setNotes] = useState('');

    useEffect(() => { document.title = 'Access Reviews - Uzima Internal'; }, []);

    const load = async () => {
        setLoading(true);
        try {
            const res = await api.get('compliance-api/access-reviews/');
            if (res.data?.status === 'success') {
                setReviews(res.data.data.reviews || []);
                setCandidates(res.data.data.candidates || []);
            }
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const startReview = async (c: Candidate) => {
        try {
            await api.post('compliance-api/access-reviews/', {
                principal_type: c.principal_type, principal_id: c.principal_id,
                principal_label: `${c.principal_type}:${c.principal_id}`,
            });
            load();
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Could not open review.' });
        }
    };

    const patchReview = async (r: Review, payload: any) => {
        try {
            const res = await api.patch(`compliance-api/access-reviews/${r.id}/`, payload);
            if (res.data?.status === 'success') {
                if (viewing?.id === r.id) setViewing(res.data.data);
                load();
            }
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Could not update review.' });
        }
    };

    return (
        <InternalShell activeDashboard="compliance_center" subNav={COMPLIANCE_NAV}>
            <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faUserShield} className="me-2 text-primary" />Access Reviews</h2>
            <p className="text-muted mb-4">Periodic least-privilege attestation over who holds which dashboards</p>

            {loading ? (
                <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
            ) : (
                <div className="row">
                    <div className="col-lg-5 mb-4">
                        <div className="card border-0 shadow-sm h-100">
                            <div className="card-header bg-white"><strong>Review candidates</strong> <span className="text-muted small">— ranked by live access held</span></div>
                            <div className="card-body p-0">
                                {candidates.length === 0 ? (
                                    <div className="text-center py-4 text-muted">No active grants to review.</div>
                                ) : (
                                    <table className="table table-hover align-middle mb-0">
                                        <thead className="table-light"><tr><th>Principal</th><th>Grants</th><th></th></tr></thead>
                                        <tbody>
                                            {candidates.map((c, i) => (
                                                <tr key={i}>
                                                    <td className="small">{c.principal_type}:{c.principal_id}</td>
                                                    <td><span className="badge bg-primary">{c.grant_count}</span></td>
                                                    <td className="text-end">
                                                        <button className="btn btn-sm btn-outline-primary" onClick={() => startReview(c)}>
                                                            <FontAwesomeIcon icon={faPlay} className="me-1" />Review
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="col-lg-7 mb-4">
                        <div className="card border-0 shadow-sm h-100">
                            <div className="card-header bg-white"><strong>Reviews</strong></div>
                            <div className="card-body p-0">
                                {reviews.length === 0 ? (
                                    <div className="text-center py-4 text-muted">No reviews opened yet.</div>
                                ) : (
                                    <table className="table table-hover align-middle mb-0">
                                        <thead className="table-light"><tr><th>Principal</th><th>Grants at review</th><th>Outcome</th><th>Reviewed</th></tr></thead>
                                        <tbody>
                                            {reviews.map(r => (
                                                <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => { setViewing(r); setNotes(r.notes); }}>
                                                    <td className="small">{r.principal_label || `${r.principal_type}:${r.principal_id}`}</td>
                                                    <td><span className="badge bg-secondary">{r.grants_at_review.length}</span></td>
                                                    <td><span className={`badge bg-${OUTCOME_COLORS[r.outcome]}`}>{r.outcome}</span></td>
                                                    <td className="small">{r.reviewed_at ? new Date(r.reviewed_at).toLocaleDateString() : '—'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {viewing && (
                <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Access review — {viewing.principal_label || `${viewing.principal_type}:${viewing.principal_id}`}</h5>
                                <button type="button" className="btn-close" onClick={() => setViewing(null)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <div className="text-muted small mb-1">Dashboards held at time of review</div>
                                    <div className="d-flex gap-1 flex-wrap">
                                        {viewing.grants_at_review.length === 0
                                            ? <span className="text-muted">None</span>
                                            : viewing.grants_at_review.map(g => <span key={g} className="badge bg-light text-dark">{g}</span>)}
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label small">Outcome</label>
                                    <select className="form-select form-select-sm" value={viewing.outcome} onChange={(e) => patchReview(viewing, { outcome: e.target.value })}>
                                        {OUTCOMES.map(o => <option key={o} value={o}>{o}</option>)}
                                    </select>
                                    <div className="form-text">Recording an outcome here does not itself change access — revoke through Super Admin's grant administration so there is one revocation path.</div>
                                </div>
                                <label className="form-label small">Notes</label>
                                <textarea className="form-control" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
                                {viewing.reviewed_by && <div className="form-text">Reviewed by {viewing.reviewed_by}</div>}
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-light" onClick={() => setViewing(null)}>Close</button>
                                <button className="btn btn-primary" onClick={() => patchReview(viewing, { notes })}>Save Notes</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </InternalShell>
    );
};

export default AccessReviews;
